/**
 * ErrorBoundary component to catch React rendering errors.
 *
 * Prevents blank screens when components crash. Captures errors to PostHog
 * for tracking and provides a fallback UI with a "Try again" option.
 *
 * Usage:
 * Wrap the app or route sections in ErrorBoundary:
 *
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 */

import React, { Component, ErrorInfo, ReactNode } from 'react'
import posthog from 'posthog-js'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so next render shows fallback UI
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to PostHog for tracking
    console.error('React ErrorBoundary caught error:', error, errorInfo)

    // Capture to PostHog with component stack trace
    posthog.captureException(error, {
      component_stack: errorInfo.componentStack,
      error_boundary: true,
    })
  }

  handleReset = (): void => {
    // Reset error state and try rendering again
    this.setState({
      hasError: false,
      error: null,
    })
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '2rem',
            textAlign: 'center',
            backgroundColor: '#f9fafb',
          }}
        >
          <div
            style={{
              maxWidth: '32rem',
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              padding: '2rem',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            }}
          >
            <h1
              style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '0.5rem',
              }}
            >
              Something went wrong
            </h1>
            <p
              style={{
                color: '#6b7280',
                marginBottom: '1.5rem',
              }}
            >
              We've been notified and are working on a fix. Please try again.
            </p>
            {this.state.error && (
              <details
                style={{
                  marginBottom: '1.5rem',
                  textAlign: 'left',
                  fontSize: '0.875rem',
                  color: '#4b5563',
                  backgroundColor: '#f3f4f6',
                  padding: '1rem',
                  borderRadius: '0.375rem',
                }}
              >
                <summary
                  style={{
                    cursor: 'pointer',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                  }}
                >
                  Error details
                </summary>
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.8125rem',
                  }}
                >
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                onClick={this.handleReset}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.backgroundColor = '#2563eb')
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.backgroundColor = '#3b82f6')
                }
              >
                Try again
              </button>
              <button
                onClick={() => (window.location.href = '/')}
                style={{
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.backgroundColor = '#e5e7eb')
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.backgroundColor = '#f3f4f6')
                }
              >
                Go home
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
